// [목적] admin API 클라이언트 — envelope 해제, 401 시 refresh 1회 재시도, 변경 요청 CSRF 헤더 첨부 + useAsync·useCursorList 훅
// [이유] M9 AdminPage. apps/web http.ts 는 수정 금지·패키지 미공유라 동일 쿠키 흐름을 최소 구현으로 재현.
// [사이드 이팩트] 401 → POST /auth/refresh (gw_rt 회전). 실패 시 ApiError(401) 로 로그인 안내 화면 전환.
// [수정 시 고려사항] 쿠키명 gw_csrf / 헤더 x-csrf-token 은 API CsrfGuard 와 동기. same-origin(프록시) 전제.

import { useCallback, useEffect, useRef, useState } from 'react';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

function csrfToken(): string {
  const m = document.cookie.match(/(?:^|;\s*)gw_csrf=([^;]+)/);
  return m?.[1] ? decodeURIComponent(m[1]) : '';
}

export async function api<T>(path: string, init: RequestInit = {}, retried = false): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);
  if (init.body) headers.set('Content-Type', 'application/json');
  if (method !== 'GET') headers.set('x-csrf-token', csrfToken());

  const res = await fetch(path, { ...init, headers, credentials: 'same-origin' });
  if (res.status === 401 && !retried) {
    const refreshed = await fetch('/auth/refresh', { method: 'POST', credentials: 'same-origin' });
    if (refreshed.ok) return api<T>(path, init, true);
  }
  const body = (await res.json().catch(() => null)) as {
    data?: T;
    error?: { code: string; message: string } | null;
  } | null;
  if (!res.ok || !body) {
    throw new ApiError(
      res.status,
      body?.error?.code ?? 'UNKNOWN',
      body?.error?.message ?? `요청 실패 (${res.status})`,
    );
  }
  return body.data as T;
}

function toApiError(e: unknown): ApiError {
  return e instanceof ApiError ? e : new ApiError(0, 'NETWORK', '네트워크 오류');
}

/**
 * [목적] 비동기 로드 상태(loading/error/data) + reload 공통 훅
 * [이유] 탭마다 같은 로딩·에러 처리 — 라이브러리(react-query) 추가 금지 조건
 * [사이드 이팩트] key 변경·reload 시 재요청. 언마운트·재요청 후 도착한 이전 응답은 alive 플래그로 무시.
 * [수정 시 고려사항] 캐시 없음 — 탭 전환 시 매번 재요청. fn 은 key 로만 재생성 판단(클로저 최신값 주의).
 */
export function useAsync<T>(fn: () => Promise<T>, key = '') {
  const [state, setState] = useState<{ data?: T; error?: ApiError; loading: boolean }>({
    loading: true,
  });
  const [tick, setTick] = useState(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: undefined }));
    fnRef
      .current()
      .then((data) => alive && setState({ data, loading: false }))
      .catch((e: unknown) => alive && setState({ error: toApiError(e), loading: false }));
    return () => {
      alive = false;
    };
  }, [key, tick]);

  return { ...state, reload: useCallback(() => setTick((t) => t + 1), []) };
}

export type Role = 'user' | 'editor' | 'admin';
export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

/**
 * [목적] 커서 목록 훅 — 첫 페이지 로드 + "더 보기" 누적 + reload
 * [이유] 수집 이력·신고 탭 공통 키셋 페이지네이션(API nextCursor 계약)
 * [사이드 이팩트] key(필터) 변경 시 목록 초기화 후 첫 페이지 재요청.
 * [수정 시 고려사항] 항목 수정 후 로컬 갱신은 호출부가 reload 로 처리(단순성 우선).
 */
export function useCursorList<T>(load: (cursor?: string) => Promise<Page<T>>, key = '') {
  const [items, setItems] = useState<T[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError>();
  const loadRef = useRef(load);
  loadRef.current = load;
  const seq = useRef(0);

  const fetchPage = useCallback(async (cursor?: string) => {
    const id = ++seq.current;
    setLoading(true);
    setError(undefined);
    try {
      const page = await loadRef.current(cursor);
      if (id !== seq.current) return;
      setItems((prev) => (cursor ? [...prev, ...page.items] : page.items));
      setNext(page.nextCursor);
    } catch (e) {
      if (id === seq.current) setError(toApiError(e));
    } finally {
      if (id === seq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchPage();
  }, [fetchPage, key]);

  return {
    items,
    loading,
    error,
    hasMore: next !== null,
    more: () => next && fetchPage(next),
    reload: () => fetchPage(),
  };
}
